/**
 * Members and roles.
 *
 * Visual Reference: `design/screens/settings-members.html` — states default,
 * **atlimit**, loading, empty, error, at 1440px and 375px.
 *
 * The screen's own design note is the specification for the top card: "the seat
 * rule is stated in words before any number is broken down, so nobody has to
 * reconcile 3 of 5 against a list of ten people. Reviewers and viewers are
 * marked free in the breakdown itself." PRD §2.6 supplies the sentence, and it
 * "must not need a footnote".
 *
 * NOTHING HERE ENFORCES ANYTHING. RULE-009: "UI hiding is presentation only,
 * never enforcement. Removing UI restrictions in the browser must grant no
 * additional access." Every control this screen hides or disables — the Owner's
 * missing role select, the disabled Invite button at the cap — has a
 * server-side check behind it in `services/members`, and
 * `test/members-adversarial.test.ts` is what proves it.
 *
 * Story 4.1.
 */
import { useCallback, useEffect, useState } from 'react'
import { apiDelete, apiGet, apiPatch, apiPost, ApiFailure } from '../../lib/api'
import { relative } from '../../lib/relative'
import { useMe } from '../../shell/useMe'
import { Button } from '../../ui/Button'
import { Field } from '../../ui/Field'
import { Modal } from '../../ui/Modal'
import { Pill } from '../../ui/Pill'
import { Select } from '../../ui/Select'
import { Skeleton } from '../../ui/Skeleton'
import { Menu, MenuButton } from '../../shell/Menu'
import { Avatar } from '../../ui/Avatar'

/* ── the shape the API returns (services/members) ───────────────────────── */

type Role = 'owner' | 'admin' | 'editor' | 'reviewer' | 'viewer' | 'client'

type MembersView = {
  seats: {
    used: number
    limit: number
    atLimit: boolean
    breakdown: { label: string; count: number; free: boolean }[]
  }
  people: {
    userId: string
    name: string
    email: string
    role: Role
    workspaceNames: string[]
    lastSeenAt: number | null
    isOwner: boolean
    seat: boolean
  }[]
  invitations: {
    id: string
    email: string
    role: Role
    roleLabel: string
    workspaceName: string | null
    invitedAt: number
  }[]
  workspaces: { id: string; name: string }[]
  roles: { role: Role; label: string; definition: string; assignable: boolean }[]
}

/** `-1` is unlimited — `services/entitlements`' `UNLIMITED`. */
const UNLIMITED = -1

export function Members() {
  const me = useMe()
  const orgId = me.status === 'ready' ? (me.me.orgs[0]?.id ?? null) : null

  const [view, setView] = useState<MembersView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [inviting, setInviting] = useState(false)

  const load = useCallback(async () => {
    if (!orgId) return
    setError(null)
    try {
      setView(await apiGet<MembersView>(`/v1/orgs/${orgId}/members`))
    } catch (failure) {
      setView(null)
      setError(failure instanceof ApiFailure ? failure.message : 'Something went wrong.')
    }
  }, [orgId])

  useEffect(() => {
    void load()
  }, [load])

  /** Every mutation ends the same way: reload, or show what the server said. */
  const act = async (key: string, run: () => Promise<unknown>) => {
    setBusy(key)
    setError(null)
    try {
      await run()
      await load()
    } catch (failure) {
      setError(failure instanceof ApiFailure ? failure.message : 'Something went wrong.')
    } finally {
      setBusy(null)
    }
  }

  /**
   * The heading, and the button that holds its place.
   *
   * `withInvite` is false on ONE state — `empty`, whose mockup frame draws the
   * heading alone, because the only "Invite someone" there is the empty card's
   * own primary button (`DESIGN-SYSTEM.md` §2: "an empty state is an invitation
   * to act"). Two identical buttons a few pixels apart is not what it draws.
   *
   * On loading and error the mockup DOES draw it, so it renders — disabled,
   * because the form it opens needs the role list and the seat count that have
   * not arrived. Same principle as `Button`'s own `loading` prop: the control
   * holds its place rather than appearing when the data does.
   */
  const headingWith = (withInvite: boolean) => (
    <div className="flex flex-wrap items-end justify-between gap-x-mdx-24 gap-y-mdx-16">
      <h1 className="m-0 min-w-0 flex-1 text-mdx-h1 font-mdx-bold leading-mdx-tight tracking-mdx-h1">
        Members and roles
      </h1>
      {withInvite ? (
        <div className="flex shrink-0 flex-wrap items-center gap-mdx-12">
          {view?.seats.atLimit ? (
            <a
              href="/billing"
              className="text-mdx-15 font-mdx-semibold text-mdx-brand-deep no-underline"
            >
              Upgrade
            </a>
          ) : null}
          <Button
            onClick={() => setInviting(true)}
            disabled={!view || view.seats.atLimit}
            title={
              view?.seats.atLimit
                ? 'Every seat is in use. Free one or upgrade the plan to invite somebody who needs one.'
                : undefined
            }
          >
            Invite someone
          </Button>
        </div>
      ) : null}
    </div>
  )

  const heading = headingWith(true)

  /* ── loading ──────────────────────────────────────────────────────────── */
  if (!view && !error) {
    return (
      <>
        {heading}
        <Card>
          <Skeleton className="h-mdx-22 w-mdx-measure-44 max-w-full" />
          <Skeleton className="h-mdx-8 w-full rounded-mdx-pill" />
        </Card>
        <Card>
          {/* Six rows at the real geometry, so nothing jumps when data lands. */}
          {Array.from({ length: 6 }, (_, index) => (
            <div key={index} className="flex items-center gap-mdx-16 px-mdx-16 py-mdx-14">
              <Skeleton className="size-mdx-avatar-sm rounded-mdx-pill" />
              <Skeleton className="h-mdx-22 flex-1" />
            </div>
          ))}
        </Card>
      </>
    )
  }

  /* ── error ────────────────────────────────────────────────────────────── */
  if (!view) {
    return (
      <>
        {heading}
        <StateCard
          shape={
            <span
              aria-hidden="true"
              className="flex size-mdx-40 shrink-0 items-center justify-center rounded-mdx-control bg-mdx-error text-mdx-20 font-mdx-bold text-mdx-white"
            >
              !
            </span>
          }
          headline="Couldn't load your team"
          body="Everyone's access is unchanged and nobody has been signed out. This page just couldn't read the member list."
          action={<Button onClick={() => void load()}>Try again</Button>}
        />
      </>
    )
  }

  const solo = view.people.length === 1 && view.invitations.length === 0

  /* ── empty — a solo account ───────────────────────────────────────────── */
  if (solo) {
    return (
      <>
        {headingWith(false)}
        <StateCard
          shape={
            <span aria-hidden="true" className="size-mdx-40 shrink-0 rounded-mdx-pill bg-mdx-line" />
          }
          headline="It's just you right now"
          body="Reviewers and viewers don't use a seat."
          action={<Button onClick={() => setInviting(true)}>Invite someone</Button>}
        />
        {inviting ? (
          <InviteModal
            view={view}
            busy={busy === 'invite'}
            error={error}
            onClose={() => setInviting(false)}
            onSubmit={(input) =>
              act('invite', async () => {
                await apiPost(`/v1/orgs/${orgId}/invitations`, input)
                setInviting(false)
              })
            }
          />
        ) : null}
      </>
    )
  }

  /* ── default and atlimit ──────────────────────────────────────────────── */
  const pendingLine =
    view.invitations.length === 0
      ? ''
      : ` · ${view.invitations.length} invitation${view.invitations.length === 1 ? '' : 's'} pending`

  return (
    <>
      {heading}

      {error ? (
        <p role="alert" className="m-0 text-mdx-15 text-mdx-error">
          {error}
        </p>
      ) : null}

      <Card className="gap-mdx-16">
        <p className="m-0 max-w-mdx-measure-60 text-mdx-17">
          <span className="font-mdx-semibold">
            {view.seats.limit === UNLIMITED
              ? `${view.seats.used} ${view.seats.used === 1 ? 'seat' : 'seats'} used.`
              : `${view.seats.used} of ${view.seats.limit} seats used.`}
          </span>
          <span className="text-mdx-slate">
            {' '}
            Seats are people who can build or change things. People who only read leads are free.
          </span>
        </p>
        <SeatBar used={view.seats.used} limit={view.seats.limit} />
        <div className="grid grid-cols-2 gap-x-mdx-20 gap-y-mdx-8 sm:grid-cols-3 lg:grid-cols-5">
          {view.seats.breakdown.map((row) => (
            <span key={row.label} className="flex items-baseline gap-mdx-8 text-mdx-15">
              <span className="text-mdx-slate">{row.label}</span>
              <span className="font-mdx-semibold tabular-nums">{row.count}</span>
              {row.free ? <span className="text-mdx-13 text-mdx-slate">free</span> : null}
            </span>
          ))}
        </div>
      </Card>

      <Card>
        <div className="flex flex-wrap items-baseline justify-between gap-x-mdx-16 gap-y-mdx-8">
          <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">People</span>
          <span className="text-mdx-13 tabular-nums text-mdx-slate">
            {view.people.length} {view.people.length === 1 ? 'person' : 'people'}
            {pendingLine}
          </span>
        </div>

        <div className="flex flex-col gap-mdx-2">
          {view.people.map((person) => (
            <Row key={person.userId}>
              <Avatar name={person.name} />
              <Identity name={person.name} detail={person.email} />

              {person.isOwner ? (
                /**
                 * The Owner's role is a PILL, not a select, and the row carries
                 * no menu — exactly as the mockup draws it, and exactly what the
                 * story asks for. `screen-manifest.md` records this row as
                 * UNRESOLVED ("the mockup renders a role select on every row
                 * including the Owner's"); the export does not, and the export
                 * is the source of truth.
                 */
                <Pill tone="brand">Owner</Pill>
              ) : (
                <Select
                  label={`${person.name}'s role`}
                  className="flex-1 basis-mdx-row-control"
                  value={person.role}
                  disabled={busy === person.userId}
                  onChange={(event) =>
                    void act(person.userId, () =>
                      apiPatch(`/v1/orgs/${orgId}/members/${person.userId}`, {
                        role: event.target.value,
                      }),
                    )
                  }
                >
                  {view.roles
                    .filter((role) => role.assignable)
                    .map((role) => (
                      <option key={role.role} value={role.role}>
                        {role.label} — {role.definition}
                      </option>
                    ))}
                </Select>
              )}

              <Pill>{person.workspaceNames.length ? person.workspaceNames.join(' · ') : 'All workspaces'}</Pill>
              <Meta>{lastSeen(person.lastSeenAt)}</Meta>

              {person.isOwner ? (
                <span className="w-mdx-icon-button shrink-0" />
              ) : (
                <RowMenu label={`More actions for ${person.name}`}>
                  <MenuButton
                    onClick={() =>
                      void act(person.userId, () =>
                        apiDelete(`/v1/orgs/${orgId}/members/${person.userId}`),
                      )
                    }
                  >
                    Remove from workspace
                  </MenuButton>
                </RowMenu>
              )}
            </Row>
          ))}

          {view.invitations.map((invitation) => (
            <Row key={invitation.id} tinted>
              <Avatar name={invitation.email} muted />
              <Identity name={invitation.email} />
              {/* The one pill on this screen that carries a STATE, so the one
                  that gets the dot — on `--mdx-review-tint`, as drawn. */}
              <Pill tone="review" dot>
                Invited {relative(invitation.invitedAt)}
              </Pill>
              <Pill>
                {invitation.roleLabel}
                {invitation.workspaceName ? ` · ${invitation.workspaceName}` : ''}
              </Pill>
              <Button
                variant="secondary"
                size="small"
                disabled={busy === invitation.id}
                onClick={() =>
                  void act(invitation.id, () =>
                    apiPost(`/v1/orgs/${orgId}/invitations/${invitation.id}/resend`, {}),
                  )
                }
              >
                Resend
              </Button>
              <RowMenu label={`More actions for ${invitation.email}'s invitation`}>
                <MenuButton
                  onClick={() =>
                    void act(invitation.id, () =>
                      apiDelete(`/v1/orgs/${orgId}/invitations/${invitation.id}`),
                    )
                  }
                >
                  Revoke invitation
                </MenuButton>
              </RowMenu>
            </Row>
          ))}
        </div>
      </Card>

      {inviting ? (
        <InviteModal
          view={view}
          busy={busy === 'invite'}
          error={error}
          onClose={() => setInviting(false)}
          onSubmit={(input) =>
            act('invite', async () => {
              await apiPost(`/v1/orgs/${orgId}/invitations`, input)
              setInviting(false)
            })
          }
        />
      ) : null}
    </>
  )
}

/* ── the pieces ─────────────────────────────────────────────────────────── */

/** `DESIGN-SYSTEM.md` §2 Card — white, border, radius 16, padding 24, shadow. */
function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <section
      className={[
        'flex flex-col gap-mdx-20 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </section>
  )
}

/**
 * The empty and error frames, which `DESIGN-SYSTEM.md` §2 says are the SAME
 * geometry — and the mockup draws them so, down to the pixel: `48px 28px`
 * padding, `14px` gap, a 40px shape, a 17px/600 headline on `--mdx-track-h3`, a
 * 15px slate line at 52ch, then one primary button.
 *
 * The only difference is the shape: a `--mdx-line` circle for empty, a filled
 * `--mdx-error` square with a white `!` for error. §2's "icon in `--mdx-error`"
 * means the shape IS the error colour, not a red glyph on nothing.
 */
function StateCard({
  shape,
  headline,
  body,
  action,
}: {
  shape: React.ReactNode
  headline: string
  body: string
  action: React.ReactNode
}) {
  return (
    <section className="flex flex-col items-center gap-mdx-14 rounded-mdx-card border border-mdx-line bg-mdx-white px-mdx-28 py-mdx-48 text-center shadow-mdx-card">
      {shape}
      <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">{headline}</span>
      <span className="max-w-mdx-measure-52 text-mdx-15 text-mdx-slate">{body}</span>
      {action}
    </section>
  )
}

/**
 * §2 Table/list row — padding 14px 16px, radius 10, no dividers, 2px gap.
 *
 * `tinted` is the pending-invitation row, which the mockup fills with
 * `--mdx-ground` and gives no hover: it is not a person yet, and a row that
 * lights up under the pointer reads as something to click into.
 */
function Row({ tinted = false, children }: { tinted?: boolean; children: React.ReactNode }) {
  return (
    <div
      className={[
        'flex flex-wrap items-center gap-x-mdx-16 gap-y-mdx-10 rounded-mdx-control px-mdx-16 py-mdx-14',
        tinted
          ? 'bg-mdx-ground'
          : 'transition-colors duration-mdx-swift ease-mdx-swift hover:bg-mdx-ground',
      ].join(' ')}
    >
      {children}
    </div>
  )
}

function Identity({ name, detail }: { name: string; detail?: string }) {
  return (
    <span className="flex min-w-0 flex-1 basis-mdx-row-identity flex-col">
      <span className="truncate text-mdx-15 font-mdx-semibold">{name}</span>
      {detail ? <span className="truncate text-mdx-13 text-mdx-slate">{detail}</span> : null}
    </span>
  )
}

/** The right-aligned meta column — `--mdx-meta-column`, 44 uses system-wide. */
function Meta({ children }: { children: React.ReactNode }) {
  return (
    <span className="min-w-mdx-meta-column shrink-0 text-right text-mdx-13 text-mdx-slate">
      {children}
    </span>
  )
}

/**
 * The `⋯` control and its menu.
 *
 * `shell/Menu.tsx` already carries the behaviour Story 3.8 wrote — Escape
 * closes, an outside click closes, focus returns to the trigger — so this is
 * the trigger's box, read off the row: `--mdx-icon-button` square, radius
 * `--mdx-radius-control-sm`, `--mdx-border`, slate glyph.
 */
function RowMenu({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Menu
      label={label}
      triggerClassName="flex size-mdx-icon-button shrink-0 cursor-pointer items-center justify-center rounded-mdx-control-sm border border-mdx-line bg-mdx-white text-mdx-15 text-mdx-slate transition-colors duration-mdx-swift ease-mdx-swift hover:bg-mdx-ground"
      trigger={<span aria-hidden="true">⋯</span>}
    >
      {children}
    </Menu>
  )
}

/**
 * The seat meter — 8px track, `--mdx-line-soft`, pill radius, brand fill.
 *
 * The percentage is DATA, not a token: it is `used / limit`, and an unlimited
 * plan has no meaningful bar at all, so it draws none rather than a full one
 * that reads as "you are out".
 */
function SeatBar({ used, limit }: { used: number; limit: number }) {
  if (limit === UNLIMITED || limit <= 0) return null
  const percent = Math.min(100, Math.round((used / limit) * 100))
  return (
    <span
      aria-hidden="true"
      className="flex h-mdx-8 overflow-hidden rounded-mdx-pill bg-mdx-line-soft"
    >
      <span className="block h-mdx-8 rounded-mdx-pill bg-mdx-brand" style={{ width: `${percent}%` }} />
    </span>
  )
}

/**
 * The invite form.
 *
 * Q-020 → DECISION-043: `settings-members.html` draws no destination for
 * "Invite someone" in any of its five states. Built to `DESIGN-SYSTEM.md` §2's
 * Modal spec, which IS specified in full, with the three fields the route
 * requires and nothing more — confirmed as built, and to be drawn into the
 * mockups rather than redesigned here. No permission toggles at invite time:
 * RULE-005 grants them afterwards, deliberately.
 * The workspace picker appears only when it can matter — a workspace-scoped
 * role in an org with more than one workspace — because a select with one
 * option is a question nobody needs asked.
 */
function InviteModal({
  view,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  view: MembersView
  busy: boolean
  error: string | null
  onClose: () => void
  onSubmit: (input: { email: string; role: Role; workspaceId?: string }) => void
}) {
  const assignable = view.roles.filter((role) => role.assignable)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('reviewer')
  const [workspaceId, setWorkspaceId] = useState(view.workspaces[0]?.id ?? '')

  const orgScoped = role === 'admin'
  const needsWorkspace = !orgScoped && view.workspaces.length > 1

  return (
    <Modal
      title="Invite someone"
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={busy ? 'Sending the invitation' : undefined}
            onClick={() =>
              onSubmit({
                email,
                role,
                ...(orgScoped ? {} : { workspaceId: workspaceId || view.workspaces[0]?.id }),
              })
            }
          >
            Send invitation
          </Button>
        </>
      }
    >
      <Field
        label="Email address"
        type="email"
        value={email}
        autoComplete="off"
        onChange={(event) => setEmail(event.target.value)}
        helper="They'll get a link that lasts seven days."
        error={error ?? undefined}
      />
      <Select
        label="Role"
        showLabel
        value={role}
        onChange={(event) => setRole(event.target.value as Role)}
      >
        {assignable.map((option) => (
          <option key={option.role} value={option.role}>
            {option.label} — {option.definition}
          </option>
        ))}
      </Select>
      {needsWorkspace ? (
        <Select
          label="Workspace"
          showLabel
          value={workspaceId}
          onChange={(event) => setWorkspaceId(event.target.value)}
        >
          {view.workspaces.map((workspace) => (
            <option key={workspace.id} value={workspace.id}>
              {workspace.name}
            </option>
          ))}
        </Select>
      ) : null}
    </Modal>
  )
}

/* ── formatting ─────────────────────────────────────────────────────────── */

/**
 * "Active now" for a live session, and plain words for somebody who has never
 * signed in — not a dash, which reads as missing data rather than as a fact.
 */
function lastSeen(at: number | null, now = Date.now()): string {
  if (at === null) return 'Not signed in yet'
  const phrase = relative(at, now)
  return phrase === 'just now' ? 'Active now' : phrase.charAt(0).toUpperCase() + phrase.slice(1)
}
